import { consecutiveRuns } from '@/utils'
import {
  accountLanes,
  emptyCellsOfYear,
  laneNumberOf,
  type MapCell,
  type MapLane,
  type MapSpan,
  memberLanes,
  overlaps,
  placeOnTracks,
  yearsOf,
} from './map'

// A bank that mails several accounts on one statement puts one piece of paper in each of their
// lanes. Those accounts are drawn as one block, one row each, so the statement is one card that
// spans their rows and a click on it picks one thing.

// A statement card inside a block, across the rows from firstRow to lastRow. A statement whose
// rows do not sit next to each other is one piece per run of rows. Its track is the band it sits on.
export interface BlockPiece {
  span: MapSpan
  firstRow: number
  lastRow: number
  // Every row the statement covers, this piece's and any other's.
  coveredRows: number[]
}

export interface BlockRow {
  lane: MapLane
  // A year the account has no statement in is filler. The account's coverage and counts never
  // looked at it, so its months carry no coverage here either.
  emptyCells: MapCell[]
}

// One grid row: an account's row on one band.
export interface BlockSlot {
  band: number
  row: number
}

// Bands stack like a lane's tracks. The first band holds a row for every account; a later band
// holds only the rows something sits on, so a second statement in a month adds one row, not many.
export interface BlockYear {
  year: number
  rows: BlockRow[]
  pieces: BlockPiece[]
  slots: BlockSlot[]
}

const statementIdsOf = (lane: MapLane) =>
  new Set(
    lane.years.flatMap((row) =>
      row.spans.map((span) => span.view.statement.id),
    ),
  )

const sharedCount = (one: Set<string>, other: Set<string>) =>
  [...one].filter((statementId) => other.has(statementId)).length

// Accounts that share a statement are one group, and so is any account linked to them through a
// chain of shared statements. A group keeps the place of its first account.
export const laneGroups = (lanes: MapLane[]): MapLane[][] => {
  let groups: MapLane[][] = []
  for (const lane of lanes) {
    const ids = statementIdsOf(lane)
    const touching = groups.filter((group) =>
      group.some((other) => sharedCount(statementIdsOf(other), ids) > 0),
    )
    const at = touching.length > 0 ? groups.indexOf(touching[0]) : groups.length
    const rest = groups.filter((group) => !touching.includes(group))
    rest.splice(at, 0, [...touching.flat(), lane])
    groups = rest
  }
  return groups
}

// One ruled-off stretch of the map: accounts drawn together, and the member cards that spend on them.
export interface MapSection {
  lanes: MapLane[]
  members: MapLane[]
}

// A member card rides inside its primary card's section, with no line between them. A card whose
// primary is not on the map, because a filter took it off, gets a section of its own.
export const mapSections = (lanes: MapLane[]): MapSection[] => {
  const members = memberLanes(lanes)
  const sections = laneGroups(accountLanes(lanes)).map((group) => {
    const numbers = new Set(group.map(laneNumberOf))
    return {
      lanes: group,
      members: members.filter((member) => numbers.has(member.memberOf)),
    }
  })
  const placed = new Set(sections.flatMap((section) => section.members))
  const alone = members
    .filter((member) => !placed.has(member))
    .map((member) => ({ lanes: [], members: [member] }))
  return [...sections, ...alone]
}

// A block can hold two primary cards, and each names its own member cards.
export const membersByPrimary = (members: MapLane[]): MapLane[][] => {
  const byPrimary = new Map<string | null, MapLane[]>()
  for (const member of members) {
    byPrimary.set(member.memberOf, [
      ...(byPrimary.get(member.memberOf) ?? []),
      member,
    ])
  }
  return [...byPrimary.values()]
}

// A card can only span rows that sit next to each other, so each account goes beside the one it
// shares the most statements with. The chain starts at an end: starting in the middle would leave
// one of that account's partners on the far side.
export const orderedRows = (lanes: MapLane[]): MapLane[] => {
  const ids = new Map(lanes.map((lane) => [lane, statementIdsOf(lane)]))
  const idsOf = (lane: MapLane) => ids.get(lane) ?? new Set<string>()
  const othersOf = (lane: MapLane) => lanes.filter((other) => other !== lane)
  const partnerCount = (lane: MapLane) =>
    othersOf(lane).filter((other) => sharedCount(idsOf(lane), idsOf(other)) > 0)
      .length
  const sharedWithAll = (lane: MapLane) =>
    othersOf(lane).reduce(
      (total, other) => total + sharedCount(idsOf(lane), idsOf(other)),
      0,
    )
  const mostShared = (
    candidates: MapLane[],
    score: (lane: MapLane) => number,
  ) =>
    candidates.reduce((best, lane) => (score(lane) > score(best) ? lane : best))
  const chainEnd = lanes.reduce((best, lane) =>
    partnerCount(lane) < partnerCount(best) ||
    (partnerCount(lane) === partnerCount(best) &&
      sharedWithAll(lane) < sharedWithAll(best))
      ? lane
      : best,
  )

  const ordered = [chainEnd]
  while (ordered.length < lanes.length) {
    const last = ordered[ordered.length - 1]
    const left = lanes.filter((lane) => !ordered.includes(lane))
    ordered.push(
      mostShared(left, (lane) => sharedCount(idsOf(last), idsOf(lane))),
    )
  }
  return ordered
}

// An account with nothing in this year still gets its row, as twelve empty months to file into.
const cellsOf = (lane: MapLane, year: number): MapCell[] =>
  lane.years.find((row) => row.year === year)?.cells ??
  emptyCellsOfYear(lane.accountId, year)

interface Placing {
  span: MapSpan
  rows: number[]
}

const statementsInYear = (lanes: MapLane[], year: number): Placing[] => {
  const byStatement = new Map<string, Placing>()
  lanes.forEach((lane, row) => {
    const spans = lane.years.find((held) => held.year === year)?.spans ?? []
    for (const span of spans) {
      const statementId = span.view.statement.id
      const placing = byStatement.get(statementId) ?? { span, rows: [] }
      byStatement.set(statementId, { ...placing, rows: [...placing.rows, row] })
    }
  })
  return [...byStatement.values()]
}

const sharesARow = (one: number[], other: number[]) =>
  one.some((row) => other.includes(row))

// Two statements share a band unless they cross the same month in the same account's row.
const onBands = (placings: Placing[]): Placing[] =>
  placeOnTracks(
    placings,
    (placing) => placing.span,
    (placing, track) => ({ ...placing, span: { ...placing.span, track } }),
    (one, other) =>
      overlaps(one.span, other.span) && sharesARow(one.rows, other.rows),
  )

const piecesOf = (placing: Placing): BlockPiece[] =>
  consecutiveRuns(placing.rows).map((run) => ({
    span: placing.span,
    firstRow: run[0],
    lastRow: run[run.length - 1],
    coveredRows: placing.rows,
  }))

const slotsOf = (rowCount: number, placed: Placing[]): BlockSlot[] => {
  const used = new Set(
    placed.flatMap((placing) =>
      placing.rows.map((row) => `${placing.span.track}:${row}`),
    ),
  )
  const bandCount = placed.reduce(
    (most, placing) => Math.max(most, placing.span.track + 1),
    1,
  )
  const slots: BlockSlot[] = []
  for (let band = 0; band < bandCount; band += 1) {
    for (let row = 0; row < rowCount; row += 1) {
      if (band === 0 || used.has(`${band}:${row}`)) slots.push({ band, row })
    }
  }
  return slots
}

const blockYear = (lanes: MapLane[], year: number): BlockYear => {
  const placed = onBands(statementsInYear(lanes, year))
  return {
    year,
    rows: lanes.map((lane) => ({
      lane,
      emptyCells: cellsOf(lane, year).filter((cell) => cell.views.length === 0),
    })),
    pieces: placed.flatMap(piecesOf),
    slots: slotsOf(lanes.length, placed),
  }
}

export const blockYears = (lanes: MapLane[]): BlockYear[] =>
  yearsOf(lanes).map((year) => blockYear(lanes, year))

// Every row a run of pieces covers sits on its band, so the run's rows are next to each other in
// the grid as well.
export const gridRowOf = (year: BlockYear, band: number, row: number) =>
  year.slots.findIndex((slot) => slot.band === band && slot.row === row) + 1
